import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';

import { toastError } from '@/components/ui/toast';
import { useTokenPairStore } from '@/features/tokenPairs';
import { useSpeedBackend } from '@/features/wallet';
import type { UserTrade } from '@/lib/api/speed';
import { queryKeys } from '@/lib/query';

import { coinKey, selectIsRealMode, useTradingStore } from '../store';

import { useActiveUserTrades } from './useActiveUserTrades';

// Minimum spacing between ensure-triggers attempts. This keeps us from calling the
// (agent-signing, rate-limited 6/60s) endpoint on every refresh of the active-trades
// query, while still letting a `pending`/error case retry later — not in a tight loop.
// Comfortably under the backend's 6-per-60s limit.
//
// That query is now push-driven with a 30s backstop poll (it used to poll every ~1s), so
// a retry that finds nothing new can wait up to the backstop rather than this cooldown.
const ENSURE_TRIGGERS_COOLDOWN_MS = 15_000;
const ENSURE_TRIGGERS_ERROR_TOAST_MS = 10_000;

// Module-level guards shared across every mounted instance of this hook, so the
// (idempotent) endpoint is hit at most once per cooldown window even if the
// desktop SpeedTradeRail and mobile MobileControlPanel ever mount it at once.
let lastEnsureAttemptAt = 0;
let ensureInFlight = false;

/**
 * A real OPEN trade requested a TP/SL leg that was never installed — the
 * requested price is present but its native trigger-order id is still null.
 * Pending trades are ignored (the position isn't open yet, so nothing to install).
 */
function tradeNeedsTriggers(trade: UserTrade): boolean {
    if (trade.status !== 'Open') return false;
    // `?? null` collapses `undefined` (absent field) into `null` so a
    // requested-but-missing leg is "requested price present, oid still null".
    const missingTp =
        (trade.takeProfit ?? null) !== null && (trade.tpOid ?? null) === null;
    const missingSl =
        (trade.stopLoss ?? null) !== null && (trade.slOid ?? null) === null;
    return missingTp || missingSl;
}

/**
 * A limit-origin OPEN trade whose entry may still be partly resting. Hyperliquid
 * places an order's own TP/SL only once the order FULLY fills, so until then the
 * filled part has no protection unless MoonHub installs cover legs for it. Asks
 * for a pass when the row was never checked, or when the live position has grown
 * past the size the last pass protected. `liveSizes` is keyed by `coinKey`.
 */
export function tradeNeedsLimitCover(
    trade: UserTrade,
    liveSizes: Readonly<Record<string, number>>,
): boolean {
    if (trade.status !== 'Open') return false;
    if ((trade.limitPrice ?? null) === null || trade.limitEntryDone === true) {
        return false;
    }
    if (
        (trade.takeProfit ?? null) === null &&
        (trade.stopLoss ?? null) === null
    ) {
        return false;
    }
    const covered =
        (trade.limitCoverSz ?? null) !== null
            ? Number(trade.limitCoverSz)
            : NaN;
    if (!Number.isFinite(covered)) return true;
    const key = coinKey(trade.pairBase);
    const live = key === null ? undefined : liveSizes[key];
    return live !== undefined && live > covered * (1 + 1e-9);
}

/**
 * Live position sizes as one primitive string, so the selector is stable between
 * unchanged polls (a fresh object per read would re-render on every store write).
 */
function selectLiveSizesKey(
    s: ReturnType<typeof useTradingStore.getState>,
): string {
    return Object.entries(s.positionsByCoin)
        .map(([key, slot]) => `${key}=${slot.livePosition?.size ?? ''}`)
        .join('|');
}

function parseLiveSizesKey(key: string): Record<string, number> {
    const sizes: Record<string, number> = {};
    for (const entry of key.split('|')) {
        const at = entry.lastIndexOf('=');
        if (at <= 0) continue;
        const size = Number(entry.slice(at + 1));
        if (entry.slice(at + 1) !== '' && Number.isFinite(size)) {
            sizes[entry.slice(0, at)] = size;
        }
    }
    return sizes;
}

interface UseEnsureTriggersResult {
    /**
     * True (real mode only) while an OPEN trade still has a requested-but-missing
     * TP/SL leg. Gates the auto-install effect; self-clears once the installed
     * oids populate on the next trades refetch. Deliberately NOT surfaced in the
     * UI — the position is already live, so the restore runs silently.
     */
    needsTriggers: boolean;
    /** True while the (long-running) ensure-triggers POST is in flight. */
    isRestoring: boolean;
}

/**
 * Detects the ambiguous-transport TP/SL recovery case and repairs it. When a
 * speed-trade opened via the recovery path (the open timed out on the FE but
 * Hyperliquid accepted it; factions later flipped it to Open) requested TP/SL but
 * the native trigger orders were never installed, this auto-calls the MoonHub
 * `POST /speed/trade/ensure-triggers` endpoint (cookie/OAuth-bearing — it
 * agent-signs, which a background job can't) to install the missing legs.
 *
 * Anti-spam: the call is fired AT MOST once per {@link ENSURE_TRIGGERS_COOLDOWN_MS}
 * window (module-level guard + in-flight flag), never per render/poll. On success
 * the trades queries are invalidated so the oids populate and `needsTriggers`
 * clears; if the response reports `pending > 0` (a trade's size isn't backfilled
 * yet) `needsTriggers` stays true and the next post-cooldown poll retries once —
 * not a tight loop. The whole flow is silent on success (the position is already
 * live; only the protective legs are being reinstated) — ONLY a failure toasts,
 * and it retries on the next detection.
 */
export function useEnsureTriggers(): UseEnsureTriggersResult {
    const backend = useSpeedBackend();
    const queryClient = useQueryClient();
    const isRealMode = useTradingStore(selectIsRealMode);
    const networkId = useTokenPairStore((s) => s.networkId);

    // subscribes to the shared active-trades query (dedup'd by key — no extra
    // network). `dataUpdatedAt` ticks on every poll so the effect below is
    // re-evaluated each poll; the cooldown decides whether to actually fire.
    const { data: activeTrades, dataUpdatedAt } = useActiveUserTrades();

    // a partly filled limit position needs a new cover pass whenever its live
    // size grows (the position poll updates it every few seconds).
    const liveSizesKey = useTradingStore(selectLiveSizesKey);

    const needsTriggers = useMemo(() => {
        if (!isRealMode) return false;
        const liveSizes = parseLiveSizesKey(liveSizesKey);
        return (activeTrades ?? []).some(
            (trade) =>
                tradeNeedsTriggers(trade) ||
                tradeNeedsLimitCover(trade, liveSizes),
        );
    }, [isRealMode, activeTrades, liveSizesKey]);

    const { mutate, isPending } = useMutation({
        mutationFn: async () => {
            if (!backend) {
                throw new Error('Not authenticated.');
            }
            return backend.ensureTriggers();
        },
        onSuccess: () => {
            // refetch so the freshly installed oids populate and `needsTriggers`
            // self-clears; history too, in case a leg already fired/closed.
            // no success toast — the restore is invisible to the user by design.
            void queryClient.invalidateQueries({
                queryKey: queryKeys.trades.active(networkId ?? 0),
            });
            void queryClient.invalidateQueries({
                queryKey: queryKeys.trades.history(networkId ?? 0),
            });
            // `pending > 0` → some trades' size isn't backfilled yet; needsTriggers
            // stays true and the cooldown lets the next poll retry (not a loop).
        },
        onError: (error) => {
            toastError(error, {
                operation: 'Stop-loss / take-profit restore',
                duration: ENSURE_TRIGGERS_ERROR_TOAST_MS,
            });
        },
        onSettled: () => {
            ensureInFlight = false;
        },
    });

    // Auto-install missing legs. Re-evaluated on every trades poll (via
    // `dataUpdatedAt`), but the cooldown + in-flight guard fire the endpoint at
    // most once per window — never per render/poll.
    useEffect(() => {
        if (!isRealMode || !needsTriggers || isPending || !backend) return;
        if (ensureInFlight) return;
        const now = Date.now();
        if (now - lastEnsureAttemptAt < ENSURE_TRIGGERS_COOLDOWN_MS) return;
        lastEnsureAttemptAt = now;
        ensureInFlight = true;
        mutate();
    }, [isRealMode, needsTriggers, isPending, backend, mutate, dataUpdatedAt]);

    return { needsTriggers, isRestoring: isPending };
}
